import axios, { AxiosInstance, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { API_CONFIG } from '../config/api';
import { ApiError, ApiResponse } from '../types/api';
import { useAuthStore } from '../store/authStore';
import { getCookie, cookieKeys, clearAuthCookies } from './cookies';
import { resolveAuditEvent } from './auditResolver';
import { auditService } from '../services/auditService';

// Unique metadata key used to pass captured request info to the response interceptor
const AUDIT_META_KEY = '__auditMeta__' as const;

interface AuditMeta {
  method: string;
  url: string;
  timestamp: string;
}

function getAdminSnapshotForAudit() {
  const state = useAuthStore.getState();
  const user = state.user;
  const { useAuditStore } = require('../store/auditStore');
  const sessionId: string = useAuditStore.getState().sessionId ?? `sess_${Date.now()}`;
  const fullName = user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(' ') || 'Unknown';
  return {
    sessionId,
    adminId: user?.id ?? state.staffUserId ?? 'unknown',
    adminName: fullName,
    adminRole: state.role ?? 'unknown',
    adminEmail: user?.email ?? 'unknown',
  };
}

// Constants
const AUTH_ENDPOINTS = [
  '/auth/login',
  '/auth/logout',
  '/auth/refresh',
  '/auth/admin/forgot-password',
  '/auth/admin/reset-password',
];

const AUDIT_LOG_ENDPOINT = '/admin/audit-logs';

const PUBLIC_AUTH_ROUTE_PREFIXES = ['/login', '/reset-password'];

// Helper functions
const isAuthEndpoint = (url?: string): boolean =>
  AUTH_ENDPOINTS.some(endpoint => url?.includes(endpoint));

const isPublicAuthRoute = (): boolean =>
  PUBLIC_AUTH_ROUTE_PREFIXES.some(prefix => window.location.pathname.startsWith(prefix)) ||
  window.location.pathname === '/';

const getAuthToken = (): string | null =>
  getCookie(cookieKeys.token) || useAuthStore.getState().token;

const shouldSkipUnauthorizedLogout = (url?: string): boolean => {
  if (isAuthEndpoint(url)) return true;
  if (url?.includes(AUDIT_LOG_ENDPOINT)) return true;
  return false;
};

const shouldAuditRequest = (url?: string): boolean => {
  if (!url) return false;
  // Unauthenticated password flows should not trigger authenticated audit logging
  if (url.includes('/auth/admin/forgot-password') || url.includes('/auth/admin/reset-password')) {
    return false;
  }
  return Boolean(getAuthToken());
};

const createApiError = (
  message: string,
  status?: number,
  code?: string,
  errors?: Record<string, string[]>
): ApiError => ({
  message,
  status,
  code,
  errors,
});

const extractErrorData = (errorData: Record<string, unknown>): { message: string; code?: string } => ({
  message: typeof errorData.message === 'string' 
    ? errorData.message 
    : typeof errorData.error === 'string' 
      ? errorData.error 
      : 'An error occurred',
  code: typeof errorData.code === 'string' 
    ? errorData.code 
    : typeof errorData.error === 'string' 
      ? errorData.error 
      : undefined,
});

let isLoggingOut = false;

const handleUnauthorizedLogout = (): void => {
  if (isLoggingOut) return;

  const hadToken = Boolean(getAuthToken());

  isLoggingOut = true;

  clearAuthCookies();
  useAuthStore.setState({
    isAuthenticated: false,
    token: null,
    refreshToken: null,
    user: null,
    role: null,
    permissions: [],
    staffUserId: null,
    error: null,
  });

  if (
    hadToken &&
    !isPublicAuthRoute() &&
    !window.location.pathname.includes('/login')
  ) {
    window.location.href = '/login?sessionExpired=true';
  }
};

// Create axios instance
const apiClient: AxiosInstance = axios.create({
  baseURL: API_CONFIG.baseURL,
  timeout: API_CONFIG.timeout,
  headers: API_CONFIG.headers,
});

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    } else if (!isAuthEndpoint(config.url)) {
      console.warn('No token found for request:', config.url);
    }

    // Stash audit metadata for the response interceptor
    const relativeUrl = config.url ?? '';
    if (resolveAuditEvent(config.method ?? 'GET', relativeUrl)) {
      (config as InternalAxiosRequestConfig & { [AUDIT_META_KEY]: AuditMeta })[AUDIT_META_KEY] = {
        method: config.method ?? 'GET',
        url: relativeUrl,
        timestamp: new Date().toISOString(),
      };
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor
apiClient.interceptors.response.use(
  (response: AxiosResponse<ApiResponse>) => {
    const meta = (response.config as any)[AUDIT_META_KEY] as AuditMeta | undefined;
    if (meta && shouldAuditRequest(meta.url)) {
      const resolved = resolveAuditEvent(meta.method, meta.url);
      if (resolved) {
        try {
          const admin = getAdminSnapshotForAudit();
          auditService.create({
            ...admin,
            ...resolved,
            timestamp: meta.timestamp,
            url: window.location.pathname + window.location.search,
            userAgent: navigator.userAgent,
            status: 'success',
          }).catch(() => undefined);
        } catch {
          // Never let audit logging crash the app
        }
      }
    }
    return response;
  },
  async (error: AxiosError<Record<string, unknown>>) => {
    const meta = (error.config as any)?.[AUDIT_META_KEY] as AuditMeta | undefined;
    if (meta && shouldAuditRequest(meta.url)) {
      const resolved = resolveAuditEvent(meta.method, meta.url);
      if (resolved) {
        try {
          const admin = getAdminSnapshotForAudit();
          const errData = (error.response?.data ?? {}) as Record<string, unknown>;
          auditService.create({
            ...admin,
            ...resolved,
            timestamp: meta.timestamp,
            url: window.location.pathname + window.location.search,
            userAgent: navigator.userAgent,
            status: 'failure',
            errorMessage:
              typeof errData.message === 'string'
                ? errData.message
                : `HTTP ${error.response?.status ?? 'unknown'}`,
          }).catch(() => undefined);
        } catch {
          // Never let audit logging crash the app
        }
      }
    }

    const errorData = (error.response?.data || {}) as Record<string, unknown>;
    const { message: errorMessage, code: errorCode } = extractErrorData(errorData);

    // Handle 401 Unauthorized: force logout only for authenticated requests
    if (error.response?.status === 401 && !shouldSkipUnauthorizedLogout(error.config?.url)) {
      handleUnauthorizedLogout();
      return Promise.reject(createApiError(
        errorMessage,
        401,
        errorCode,
        errorData.errors as Record<string, string[]> | undefined
      ));
    }

    return Promise.reject(createApiError(
      errorMessage,
      error.response?.status,
      errorCode,
      errorData.errors as Record<string, string[]> | undefined
    ));
  }
);

export default apiClient;
